import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import moment from "moment";
import React, {useContext, useEffect, useRef, useState} from "react";
import useGeneralEditUserForm from "./useGeneralEditUserForm";
import {Checkbox, Chip, FormControlLabel, FormHelperText, TextField} from "@mui/material";
import {useLocation} from "react-router-dom";
import Autocomplete from '@mui/material/Autocomplete';
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {useStyles} from "./GeneralEditUserForm.css";
import {useIntl} from "react-intl";
import generalConfig from "../../../../commons/associationStructure/generalConfig.json";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import AssociationLogo from "../../../../commons/utils/logo/AssociationLogo";
import hash from "object-hash";
import {Prompt} from 'react-router'

const GeneralEditUserForm = ({selectedUser, editedUserRef, setSaveButtonEnabled}) => {
    const classes = useStyles()
    const location = useLocation();

    // Get permission-based limited association structure
    const {allowedAssociationStructure, signedInUserRoleScopes, signedInUserRole} = useContext(FirebaseAuthContext);

    // Hook providing various form control functions for edited the user
    const {
        email,
        firstName,
        secondName,
        associationInfo,
        role,
        roleScopes,
        password,
        passwordRepeat,
        isReviewer,
        handleChangeFirstName,
        handleChangeSecondName,
        handleChangeDisciplines,
        handleChangeExperience,
        handleRoleChange,
        handleChangeRoleScopes,
        handleChangeEmail,
        handleChangePassword,
        handleChangePasswordRepeat,
        handleChangeIsReviewer
    } = useGeneralEditUserForm(selectedUser, editedUserRef)

    // Internationalization for simple strings
    const intl = useIntl();

    // Handling for hinting the user to save changes in case he tries to leave the form after making changes
    const initialHash = useRef(null)
    const [hasFormChanged, setHasFormChanged] = useState(false)


    useEffect(() => {
        if (location.pathname.match('/admin/users/.*/edit') || location.pathname === '/common/profile/edit') {
            if (role === 'player') {
                setSaveButtonEnabled(firstName && secondName && roleScopes.length > 0 && areDisciplinesValid() && areExperiencesValid())
            } else {
                setSaveButtonEnabled(firstName && secondName && role && roleScopes.length > 0)
            }
        }

        if (location.pathname === '/admin/users/new') {
            if (role === 'player') {
                setSaveButtonEnabled(firstName && secondName && email && roleScopes.length > 0 && areDisciplinesValid() && areExperiencesValid())
            } else {
                setSaveButtonEnabled(firstName && secondName && email && role && roleScopes.length > 0)
            }
        }

        if (location.pathname === '/signUp') {
            setSaveButtonEnabled(email && password && passwordRepeat && passwordRepeat === password && password.length >= 6 && firstName && secondName)
        }

        // Refresh the current hash and set the initial hash at the very beginning
        const currentHash = hash(editedUserRef.current, {unorderedArrays: true})
        if (!initialHash.current) {
            initialHash.current = currentHash
        }
        setHasFormChanged(initialHash.current !== currentHash)

    }, [firstName, secondName, email, password, passwordRepeat, associationInfo, roleScopes, role, roleScopes])

    const areDisciplinesValid = () => {
        return !Object.keys(associationInfo).find((scope) => associationInfo[scope].disciplines.length === 0)
    }

    const areExperiencesValid = () => {
        return !Object.keys(associationInfo).find((scope) => associationInfo[scope].experience === '')
    }

    /* The signed-in user's role must be a superset of the scopes in case he wants to update the role.
       This way we go sure that a user cannot downgrade roles of lesser scopes. */
    const isRoleChangeAllowed = (userToBeRoleChanged) => {

        // For creating new users, the role must always be set
        if (!userToBeRoleChanged) {
            return true
        }

        // Role scopes are actually always not-null - however during migration phases not all users had this pre-requisite
        if (!userToBeRoleChanged.roleScopes) {
            return true
        }

        return userToBeRoleChanged.roleScopes.every((userScope) => signedInUserRoleScopes.includes(userScope))
    }

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const showElementIfPath = (showablePaths) => {
        let found = false
        showablePaths.forEach((path) => {
            if (location.pathname.match(path)) {
                found = true
            }
        })
        return found
    }

    const getPasswordMatchInfo = () => {
        if (password && passwordRepeat) {
            if (password !== passwordRepeat || password.length < 6) {
                return <ErrorIcon className={classes.errorIcon}/>
            } else {
                return <CheckCircleIcon className={classes.successIcon}/>
            }
        }
        return ''
    }

    return (
        <div className={classes.root}>
            {/* Security prompt if the user tries to (maybe unintendendly) leave the page.
                This needs to be effective if there are changes made to the current object. */}
            <Prompt
                when={hasFormChanged}
                message={intl.formatMessage({
                    id: 'app.utils.unsavedChanges.hint',
                    defaultMessage: 'Your changes are not saved, are you sure you want to leave?'
                })}
            />

            <div className={classes.row}>
                {/* First name */}
                <FormControl className={classes.formControl}>
                    <TextField required value={firstName} onChange={handleChangeFirstName}
                               variant="outlined" margin="normal"
                               id="reg-user-firstName"
                               label={intl.formatMessage({
                                   id: 'app.users.edit.form.firstName.label',
                                   defaultMessage: 'First Name'
                               })}/>

                    <FormHelperText variant="outlined" error={!firstName} id="reg-user-firstName-help">
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.firstName.help',
                            defaultMessage: 'Enter first name'
                        }), !firstName)}
                    </FormHelperText>
                </FormControl>

                {/* Second name */}
                <FormControl className={classes.formControl}>
                    <TextField required value={secondName} onChange={handleChangeSecondName}
                               variant="outlined" margin="normal"
                               id="reg-user-secondName"
                               label={intl.formatMessage({
                                   id: 'app.users.edit.form.secondName.label',
                                   defaultMessage: 'Second Name'
                               })}/>

                    <FormHelperText variant="outlined" error={!secondName} id="reg-user-second-help">
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.secondName.help',
                            defaultMessage: 'Enter second name'
                        }), !secondName)}
                    </FormHelperText>
                </FormControl>

                {/* Email */}
                {showElementIfPath(['/admin/users/new', '/signUp'])
                    ? <FormControl className={classes.formControl}>
                        <TextField required value={email} onChange={handleChangeEmail}
                                   variant="outlined" margin="normal"
                                   id="reg-user-email"
                                   label={intl.formatMessage({
                                       id: 'app.users.edit.form.email.label',
                                       defaultMessage: 'Email'
                                   })}/>

                        <FormHelperText variant="outlined" error={!email} id="reg-user-email-help">
                            {showIfTrue(intl.formatMessage({
                                id: 'app.users.edit.form.email.help',
                                defaultMessage: 'Enter email'
                            }), !email)}
                        </FormHelperText>
                    </FormControl>
                    : <div className={classes.column}>
                        <div className={classes.email}>
                            {selectedUser.email}
                        </div>
                        <div className={classes.lastSignIn}>
                            {intl.formatMessage({
                                id: 'app.users.edit.form.lastSignIn.label',
                                defaultMessage: 'Last Sign-In'
                            })}: {selectedUser.lastSignIn
                            ? moment(selectedUser.lastSignIn).format('DD.MM.YYYY (HH:mm:ss)')
                            : '/'}
                        </div>
                    </div>
                }
            </div>

            <div className={classes.row}>
                {/* Role */}
                {showElementIfPath(['/admin/users/new', '/admin/users/.*/edit']) &&
                <FormControl className={classes.formControl}>
                    <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                id="reg-role-label">
                        {intl.formatMessage({
                            id: 'app.users.edit.form.role.label',
                            defaultMessage: 'Role'
                        })}
                    </InputLabel>
                    <Select labelId="reg-role-label" id="reg-role-input" aria-describedby="reg-role-help"
                            variant="outlined"
                            value={role} onChange={handleRoleChange}
                            disabled={!isRoleChangeAllowed(selectedUser)}>

                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>

                        {/* Organizers are only allowed to create/edit 'players' - therefore restrict it also in FE (restricted in BE as well of course) */}
                        {signedInUserRole === 'organizer'
                            ? <MenuItem key={'player'} value={'player'}>
                                {intl.formatMessage({
                                    id: `app.ids.role.player`,
                                    defaultMessage: 'Player'
                                })}
                            </MenuItem>
                            : generalConfig.roles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {intl.formatMessage({
                                        id: `app.ids.role.${role}`,
                                        defaultMessage: role
                                    })}
                                </MenuItem>
                            ))}
                    </Select>
                    <FormHelperText id="reg-role-help" error={!role}>
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.role.help',
                            defaultMessage: 'Enter role'
                        }), !role)}
                    </FormHelperText>
                </FormControl>}

                {/* Role Scope */}
                {showElementIfPath(['/admin/users/new', '/admin/users/.*/edit', '/common/profile/edit']) &&
                <FormControl className={classes.formControl}>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        id="reg-user-roleScopes-input"
                        aria-describedby="reg-user-roleScopes-help"
                        options={Object.keys(allowedAssociationStructure)}
                        getOptionLabel={(associationScope) =>
                            intl.formatMessage({
                                id: `app.ids.association.${associationScope}`
                            })}

                        renderInput={(params) => (
                            <TextField {...params} variant="outlined"
                                       label={intl.formatMessage({
                                           id: 'app.users.edit.form.roleScopes.label',
                                           defaultMessage: 'Scopes'
                                       })}
                                       placeholder={intl.formatMessage({
                                           id: 'app.users.edit.form.roleScopes.placeholder',
                                           defaultMessage: 'Add Scopes'
                                       })}
                            />
                        )}
                        onChange={handleChangeRoleScopes}
                        value={roleScopes}
                        disabled={!role}

                        renderTags={(tValue, getTagProps) =>
                            tValue.map((associationScope, index) => {
                                // Do not allow the deletion of scopes the user is not permitted to
                                // This is applied for admins only, because users should have the possibility to arbitrarily change their scopes
                                // The allowed association structure function already considers that it is only applied for admins only
                                return <Chip key={associationScope}
                                             {...getTagProps({index})}
                                             label={intl.formatMessage({
                                                 id: `app.ids.association.${associationScope}`
                                             })}
                                             disabled={!Object.keys(allowedAssociationStructure).includes(associationScope)}
                                />
                            })
                        }
                    />

                    <FormHelperText variant="outlined" error={roleScopes.length === 0}
                                    id="reg-users-roleScope-help">
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.roleScopes.help',
                            defaultMessage: 'Enter scopes'
                        }), roleScopes.length === 0)}
                    </FormHelperText>
                </FormControl>}

                {/* If the user is allowed to approve/reject scored scenes (only visible for admins in the manage-user section) */}
                {showElementIfPath(['/admin/users/new', '/admin/users/.*/edit']) && signedInUserRole === 'admin' && role && role !== 'player' &&
                <FormControlLabel
                    control={
                        <Checkbox value={isReviewer}
                                  checked={isReviewer}
                                  onChange={handleChangeIsReviewer}/>}
                    label={intl.formatMessage({
                        id: 'app.users.edit.form.isReviewer.label',
                        defaultMessage: 'Reviewer'
                    })}/>
                }
            </div>

            <div hidden={role !== 'player'}>

                {/* For each selected scope/association, a discipline and an experience dropdown is rendered */}
                {Object.keys(associationInfo).map((scope) => (
                    <div key={scope} className={classes.row}>

                        <div className={classes.associationLogo}>
                            <AssociationLogo association={scope} height={35} width={35}/>
                        </div>

                        {/* Disciplines for the selected scope */}
                        <FormControl className={classes.formControl}>
                            <Autocomplete
                                multiple
                                limitTags={2}
                                id="reg-user-discipline-input"
                                aria-describedby="reg-user-discipline-help"
                                options={allowedAssociationStructure[scope]?.disciplines.map((d) => d.name) || []}
                                getOptionLabel={(discipline) =>
                                    intl.formatMessage({
                                        id: `app.ids.discipline.${discipline}`
                                    })}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined"
                                               label={intl.formatMessage({
                                                   id: 'app.users.edit.form.disciplines.label',
                                                   defaultMessage: 'Disciplines'
                                               })}
                                               placeholder={intl.formatMessage({
                                                   id: 'app.users.edit.form.disciplines.placeholder',
                                                   defaultMessage: 'Add Disciplines'
                                               })}
                                    />
                                )}
                                onChange={(event, value) => handleChangeDisciplines(value, scope)}
                                value={associationInfo[scope].disciplines}
                                disabled={!signedInUserRoleScopes.includes(scope) && signedInUserRole !== 'player'}
                            />
                            <FormHelperText variant="outlined"
                                            error={associationInfo[scope].disciplines.length === 0}
                                            id="reg-users-discipline-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.users.edit.form.disciplines.help',
                                    defaultMessage: 'Enter disciplines'
                                }), associationInfo[scope].disciplines.length === 0)}
                            </FormHelperText>
                        </FormControl>

                        {/* Experience for the selected scope */}
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                        id="reg-user-experience-label">
                                {intl.formatMessage({
                                    id: 'app.users.edit.form.experience.label',
                                    defaultMessage: 'Experience'
                                })}
                            </InputLabel>
                            <Select id="reg-user-experience-input" labelId="reg-user-experience-label"
                                    aria-describedby="reg-user-experience-help"
                                    variant="outlined"
                                    value={associationInfo[scope].experience}
                                    onChange={(event) => handleChangeExperience(event, scope)}
                                    disabled={!signedInUserRoleScopes.includes(scope) && signedInUserRole !== 'player'}>

                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>

                                {generalConfig.experience.map((experience) =>
                                    <MenuItem key={experience} value={experience}>
                                        {intl.formatMessage({
                                            id: `app.ids.expertise.${experience}`
                                        })}
                                    </MenuItem>
                                )}

                            </Select>
                            <FormHelperText variant="outlined" error={!associationInfo[scope].experience}
                                            id="reg-user-experience-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.users.edit.form.experience.help',
                                    defaultMessage: 'Enter experience'
                                }), !associationInfo[scope].experience)}
                            </FormHelperText>
                        </FormControl>
                    </div>
                ))}
            </div>

            <div>
                {/* Password */}
                {showElementIfPath(['/signUp']) && <FormControl className={classes.formControl}>
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={password}
                               onChange={handleChangePassword}
                               id="reg-user-password"
                               label={intl.formatMessage({
                                   id: 'app.users.edit.form.password.label',
                                   defaultMessage: 'Password'
                               })}/>

                    <FormHelperText variant="outlined" error={!password}
                                    id="reg-user-password-help">
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.password.help',
                            defaultMessage: 'Enter password'
                        }), !password)}
                    </FormHelperText>
                </FormControl>}

                {/* Password Repeat */}
                {showElementIfPath(['/signUp']) && <FormControl className={classes.formControl}>
                    <TextField variant="outlined" margin="normal" fullWidth required type="password"
                               value={passwordRepeat}
                               onChange={handleChangePasswordRepeat}
                               id="reg-user-passwordRepeat"
                               label={intl.formatMessage({
                                   id: 'app.users.edit.form.passwordRepeat.label',
                                   defaultMessage: 'Repeat Password'
                               })}
                               InputProps={{endAdornment: getPasswordMatchInfo()}}/>

                    <FormHelperText variant="outlined" error={!passwordRepeat}
                                    id="reg-user-passwordRepeat-help">
                        {showIfTrue(intl.formatMessage({
                            id: 'app.users.edit.form.passwordRepeat.help',
                            defaultMessage: 'Repeat password'
                        }), !passwordRepeat)}
                    </FormHelperText>
                </FormControl>}
            </div>
        </div>
    );
}
export default GeneralEditUserForm